import { Input } from '@ibsys/kit';
import { FC, useEffect, useRef, useState } from 'react';
import { cn } from '@/shared/lib';

type Props = {
  value: string;
  placeholder?: string;
  disabled?: boolean;
  onSave?: (value: string) => void;
  textClassName?: string;
  inputClassName?: string;
  /** Фиксированная ширина поля ввода вместо авто-расширения по контенту */
  fixedInputWidth?: boolean;
};

export const EditableTitle: FC<Props> = (props) => {
  const {
    value,
    placeholder = '',
    disabled = false,
    onSave,
    textClassName,
    inputClassName,
    fixedInputWidth = false,
  } = props;

  const [isEditing, setIsEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (isEditing) {
      const timeoutId = setTimeout(() => {
        const inputElement = inputRef.current?.querySelector('input') as HTMLInputElement | null;
        if (inputElement) {
          inputElement.focus();
          inputElement.select();
        }
      }, 10);
      return () => clearTimeout(timeoutId);
    }
  }, [isEditing]);

  const displayValue = value?.trim() || placeholder;

  const handleTitleClick = () => {
    if (!disabled) {
      setIsEditing(true);
    }
  };

  const handleBlur: React.FocusEventHandler<HTMLInputElement> = (event) => {
    setIsEditing(false);
    const trimmedValue = event.target.value.trim();
    if (trimmedValue !== value && onSave) {
      onSave(trimmedValue);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      const inputElement = inputRef.current?.querySelector('input') as HTMLInputElement | null;
      inputElement?.blur();
    }
    if (e.key === 'Escape') {
      setIsEditing(false);
    }
  };

  if (isEditing) {
    const inputSizeClass = fixedInputWidth ? 'min-w-72 w-72 max-w-[700px]' : 'field-sizing-content max-w-[700px]';
    return (
      <div ref={inputRef as any} className="inline-flex">
        <Input
          defaultValue={value}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          size="lg"
          classNames={{
            input: `${inputSizeClass} ${inputClassName ?? ''}`,
          }}
        />
      </div>
    );
  }

  return (
    <span
      role="button"
      tabIndex={disabled ? -1 : 0}
      className={cn(textClassName, !disabled && 'cursor-pointer')}
      onClick={handleTitleClick}
      onKeyDown={(e) => {
        if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          handleTitleClick();
        }
      }}
      aria-label={disabled ? undefined : 'Edit'}
    >
      {displayValue}
    </span>
  );
};
